// Files for the admin assistant, prepared in the browser before upload: images scaled down and
// re-encoded as JPEG (Claude reads up to 1,568 px on the long side anyway; the API decodes the JPEG
// and stores a new one, refusing anything that isn't a real image), text read as text, and Excel
// workbooks turned into CSV here. The spreadsheet parser (read-excel-file, pinned) loads only when
// a workbook is dropped, and runs on the staff member's own file in their own tab: the API never
// unpacks a zip or parses spreadsheet XML, it only ever receives text.
import { api } from './api';
import type { FileRef } from './assistant';

const LONG_SIDE = 1568;
const IMAGE = /^image\/(png|jpeg|webp)$/;
const IMAGE_EXT = /\.(png|jpe?g|webp)$/i;

export const ACCEPT =
  'image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp,.md,.markdown,.txt,.csv,.xlsx,text/plain,text/markdown,text/csv';

interface Prepared {
  name: string;
  kind: 'image' | 'text';
  contentType: string;
  data: string;
}

function toBase64(buf: ArrayBuffer): string {
  let s = '';
  const b = new Uint8Array(buf);
  for (let i = 0; i < b.length; i += 0x8000) s += String.fromCharCode(...b.subarray(i, i + 0x8000));
  return btoa(s);
}

const encode = (canvas: HTMLCanvasElement, type: string, quality: number) =>
  new Promise<Blob | null>((r) => canvas.toBlob(r, type, quality));

async function image(file: File, maxBytes: number): Promise<Prepared> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error(`${file.name} couldn’t be read as an image.`);
  }
  const scale = Math.min(1, LONG_SIDE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const g = canvas.getContext('2d')!;
  // White under transparent areas, so the JPEG doesn't turn them black.
  g.fillStyle = '#ffffff';
  g.fillRect(0, 0, canvas.width, canvas.height);
  g.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  for (const q of [0.86, 0.76, 0.62, 0.48]) {
    const blob = await encode(canvas, 'image/jpeg', q);
    if (blob?.type === 'image/jpeg' && blob.size <= maxBytes)
      return {
        name: file.name,
        kind: 'image',
        contentType: 'image/jpeg',
        data: toBase64(await blob.arrayBuffer()),
      };
  }
  throw new Error(`${file.name} is too detailed to send even scaled down. Crop it and try again.`);
}

/** RFC 4180 CSV of a sheet's rows. */
function csv(rows: unknown[][]): string {
  const cell = (v: unknown) => {
    const s =
      v === null || v === undefined
        ? ''
        : v instanceof Date
          ? v.toISOString().replace('T00:00:00.000Z', '')
          : typeof v === 'boolean'
            ? v
              ? 'TRUE'
              : 'FALSE'
            : String(v);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return rows.map((r) => r.map(cell).join(',')).join('\n');
}

async function workbook(file: File): Promise<string> {
  const { default: readXlsxFile } = await import('read-excel-file/browser');
  let sheets: { sheet: string; data: unknown[][] }[];
  try {
    sheets = (await readXlsxFile(file)) as { sheet: string; data: unknown[][] }[];
  } catch {
    throw new Error(`${file.name} couldn’t be read as an Excel workbook (.xlsx).`);
  }
  const used = sheets.filter((s) => s.data.length);
  if (!used.length) throw new Error(`${file.name} has no rows.`);
  return used.length === 1
    ? csv(used[0]!.data)
    : used.map((s) => `# Sheet: ${s.sheet}\n${csv(s.data)}`).join('\n\n');
}

async function prepare(file: File, limits: { imageBytes: number; textChars: number }) {
  const lower = file.name.toLowerCase();
  if (IMAGE.test(file.type) || IMAGE_EXT.test(lower)) return image(file, limits.imageBytes);
  let text: string;
  let contentType = 'text/plain';
  let name = file.name;
  if (lower.endsWith('.xlsx')) {
    text = await workbook(file);
    contentType = 'text/csv';
    name = `${file.name} (as CSV)`;
  } else if (lower.endsWith('.csv')) {
    text = await file.text();
    contentType = 'text/csv';
  } else if (/\.(md|markdown)$/.test(lower)) {
    text = await file.text();
    contentType = 'text/markdown';
  } else if (lower.endsWith('.txt') || file.type === 'text/plain') {
    text = await file.text();
  } else {
    throw new Error(
      `${file.name}: Claude reads PNG, JPEG and WebP images, and Markdown, text, CSV and Excel (.xlsx) files.`,
    );
  }
  if (text.length > limits.textChars)
    throw new Error(
      `${file.name} is too long (${text.length.toLocaleString('en-US')} characters; at most ${limits.textChars.toLocaleString('en-US')}). Remove columns or rows Claude doesn’t need.`,
    );
  return { name, kind: 'text' as const, contentType, data: text };
}

/** Prepare and upload one file; resolves to what the next message can attach. */
export async function uploadForAssistant(
  file: File,
  limits: { imageBytes: number; textChars: number },
): Promise<FileRef & { bytes: number }> {
  const p = await prepare(file, limits);
  return api<FileRef & { bytes: number }>('/assistant/files', { body: p });
}
